import { Typography, useTheme } from 'hephaestus/web';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { useBadgeTokens } from './badge.tokens';

export const colorMap = {
  neutral: 'neutral',
  negative: 'negative',
  warning: 'warning',
  positive: 'positive',
  gray: 'gray',
  supportOne: 'supportOne',
  supportTwo: 'supportTwo',
  supportThree: 'supportThree',
  supportFour: 'supportFour',
  supportFive: 'supportFive',
  supportSix: 'supportSix',
  supportSeven: 'supportSeven',
};

/**
 * A background and foreground together, because a caller that picks one has to
 * answer for the label still being readable against the other.
 */
export type BadgeCustomColor = {
  backgroundColor: string;
  textColor: string;
};

export type BadgeProps = {
  label: string;
  color?: keyof typeof colorMap;
  /**
   * Colours from outside the palette, for the one case `color` cannot serve:
   * more things to tell apart on a screen than the palette has slots. Prefer
   * `color`, which keeps a badge on design system colours; this takes
   * precedence over it where both are given.
   */
  customColor?: BadgeCustomColor;
  variant?: 'number' | 'text';
  small?: boolean;
  sx?: any;
  testId?: string;
} & Omit<ComponentPropsWithoutRef<'span'>, 'color'>;

/**
 * Forwards ref and extra props to the root `span` so wrappers such as `Tooltip`
 * can attach to it directly.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      label,
      color = 'neutral',
      customColor,
      variant = 'text',
      small = false,
      sx = {},
      testId = 'badge',
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const tokens = useBadgeTokens(theme);
    const textColor =
      variant === 'number'
        ? tokens.number.textColor
        : (customColor?.textColor ?? tokens[variant][color].textColor);
    const bgColor =
      variant === 'number'
        ? tokens.number.backgroundColor
        : (customColor?.backgroundColor ??
          tokens[variant][color].backgroundColor);
    const borderColor =
      variant === 'number'
        ? tokens.number.borderColor
        : (customColor?.backgroundColor ?? tokens[variant][color].borderColor);
    const borderStyle = tokens[variant].borderStyle;
    const borderWidth = tokens[variant].borderWidth;
    return (
      <span {...rest} ref={ref} data-testid={testId}>
        <Typography
          data-testid={`${testId}-text`}
          variant='labelSmall'
          sx={{
            minHeight: small ? '16px' : variant === 'number' ? '16px' : '20px',
            display: 'inline-block',
            textAlign: 'center',
            color: textColor,
            backgroundColor: bgColor,
            borderRadius: tokens[variant].borderRadius,
            borderStyle,
            borderWidth,
            borderColor,
            minWidth: '16px',
            alignContent: 'center',
            padding: small ? '0px 4px' : '1px 4px 1px 4px',
            ...sx,
          }}
        >
          {label}
        </Typography>
      </span>
    );
  },
);
